import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import "@/components/feedback/feedback_testcase";
import { renderMessages } from "@/feedback/message_helpers";
import type { Context, DiffStyle, FeedbackEnvelopeContext } from "@/types/FeedbackResult";

/**
 * Renders the content of one context (group of testcases): its description, its messages and its
 * testcases. `d-feedback-context` shows it in its card, and `d-scratchpad` in the tab of the test it
 * debugs.
 *
 * @element d-feedback-context-body
 *
 * @prop {Context}                 context           - Context data (testcases, messages, …).
 * @prop {string}                  diff-style        - Forwarded to the testcases; "split" or "unified".
 * @prop {FeedbackEnvelopeContext} submissionContext - Submission context (activity path, access token).
 */
@customElement("d-feedback-context-body")
export class FeedbackContextBody extends DodonaElement {
    @property({ type: Object }) context!: Context;
    @property({ type: String }) diffStyle: DiffStyle = "split";
    @property({ type: Object }) submissionContext: FeedbackEnvelopeContext = {};

    render(): TemplateResult {
        const g = this.context;
        if (!g) return html``;

        return html`
            ${
                g.description
                    ? html`<div class="row">
                          <div class="col-12 description">
                              <d-feedback-message
                                  .format=${"plain"}
                                  .description=${g.description}
                                  .files=${g.data?.files}
                                  .activityPath=${this.submissionContext.activityPath}
                                  .accessToken=${this.submissionContext.accessToken}
                              ></d-feedback-message>
                          </div>
                      </div>`
                    : ""
            }
            ${g.messages?.length ? renderMessages(g.messages, this.submissionContext, g.data?.files) : ""}
            ${(g.groups ?? []).map(
                (tc) =>
                    html`<d-feedback-testcase
                        .testcase=${tc}
                        .diffStyle=${this.diffStyle}
                        .submissionContext=${this.submissionContext}
                        .contextFiles=${g.data?.files}
                    ></d-feedback-testcase>`,
            )}
        `;
    }
}
